import { QueryResponse } from '../services/api';

/**
 * Sanitize markdown text to avoid conflicts between money values ($123.45)
 * and LaTeX math syntax ($...$ or $$...$$).
 */
export function sanitizeMarkdown(text: string): string {
    if (!text) return "";

    let sanitized = text;

    // 1️⃣ Escape `$` in currency (but not if it's part of LaTeX \( ... \) or $$ ... $$)
    // Pattern: $123, $1,234.56 etc. → \$123
    sanitized = sanitized.replace(/(?<!\\)(\$\d[\d,]*(\.\d+)?)(?![\w\s]*[\$\)])/g, '\\$1');

    // 2️⃣ Normalize escaped newlines from backend logs like "\\n" → "\n"
    sanitized = sanitized.replace(/\\n/g, '\n');

    // 3️⃣ Optional: Clean up accidental duplicated $ escape (from double-processing)
    sanitized = sanitized.replace(/\\\\\$/g, '\\$');

    // 4️⃣ Ensure LaTeX delimiters stay intact: \( ... \), $$ ... $$
    // Sometimes backend double-escapes them → fix if needed
    sanitized = sanitized.replace(/\\\\\(/g, '\\(').replace(/\\\\\)/g, '\\)');

    return sanitized.trim();
}

function buildDataFromDataframeJson(dfJson?: string): QueryResponse['data'] | undefined {
    if (!dfJson) return undefined;
    try {
        const rows = JSON.parse(dfJson); // array of row objects
        if (!Array.isArray(rows) || rows.length === 0 || typeof rows[0] !== 'object') return undefined;

        const columns = Object.keys(rows[0]);
        return {
            tables: [{
                title: 'Result',
                columns,
                data: rows,
                row_count: rows.length,
            }],
            charts: [],
            chat_responses: [],
        };
    } catch {
        return undefined;
    }
}

export { buildDataFromDataframeJson };

function buildFallbackPlotlyChartData(
    figureJson: string,
    interactionId?: string
): NonNullable<QueryResponse['data']>['charts'][number] {
    const baseId = interactionId ? `hist_${interactionId}` : 'hist_unknown';
    return {
        title: 'Chart',
        type: 'plotly',
        data: figureJson,
        chart_id: `${baseId}_plotly_0`,
    };
}

function buildFallbackMatplotlibChartData(
    figureJson: string,
    interactionId?: string
): NonNullable<QueryResponse['data']>['charts'][number] {
    const baseId = interactionId ? `hist_${interactionId}` : 'hist_unknown';
    return {
        title: 'Matplotlib Chart',
        type: 'matplotlib',
        data: figureJson,
        chart_id: `${baseId}_matplotlib_0`,
    };
}

function normalizeChartEntry(
    ch: any,
    idx: number,
    interactionId?: string
): NonNullable<QueryResponse['data']>['charts'][number] | null {
    if (!ch || typeof ch !== 'object') return null;
    const baseId = interactionId ? `hist_${interactionId}` : 'hist_unknown';
    const title = typeof ch.title === 'string' && ch.title.trim() ? ch.title : 'Chart';

    const rawType = typeof ch.type === 'string' ? ch.type : undefined;
    const type = rawType === 'matplotlib' ? 'matplotlib' : 'plotly';

    let data = ch.data;
    if (typeof data !== 'string') {
        try {
            data = JSON.stringify(data);
        } catch {
            data = String(data ?? '');
        }
    }

    // If type is missing/incorrect, try to infer plotly from figure-shaped JSON string.
    if (type !== 'matplotlib') {
        const t = data.trim();
        if (!t.startsWith('{') && !t.includes('"layout"')) {
            // likely not a Plotly figure JSON
            // keep as plotly to avoid surprising user; UI will error if invalid
        }
    }

    const chartId =
        typeof ch.chart_id === 'string' && ch.chart_id.trim()
            ? ch.chart_id
            : `${baseId}_${type}_${idx}`;

    return {
        title,
        type,
        data,
        chart_id: chartId,
    };
}

function buildChartsFromFigureJson(figureJson?: string | null, interactionId?: string): NonNullable<QueryResponse['data']>['charts'] {
    if (!figureJson) return [];
    const trimmed = figureJson.trim();
    if (!trimmed) return [];

    // Try parse wrapper first.
    try {
        const parsed = JSON.parse(trimmed);

        // New format: an array of chart wrappers.
        if (Array.isArray(parsed)) {
            const charts = parsed
                .map((ch, idx) => normalizeChartEntry(ch, idx, interactionId))
                .filter((x): x is NonNullable<QueryResponse['data']>['charts'][number] => !!x);
            return charts;
        }

        // Another common format: { charts: [...] }
        if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).charts)) {
            const charts = (parsed as any).charts
                .map((ch: any, idx: number) => normalizeChartEntry(ch, idx, interactionId))
                .filter((x: any): x is NonNullable<QueryResponse['data']>['charts'][number] => !!x);
            return charts;
        }

        // Backward compatible: old storage might be just a Plotly figure object.
        if (parsed && typeof parsed === 'object' && (parsed as any).layout && (parsed as any).data) {
            return [buildFallbackPlotlyChartData(JSON.stringify(parsed), interactionId)];
        }
    } catch {
        // fall through to heuristic fallback
    }

    // Heuristic fallback for non-JSON strings.
    // Plotly figure JSON strings typically look like: { "data": ..., "layout": ... }
    if (trimmed.startsWith('{') || trimmed.includes('"layout"')) {
        return [buildFallbackPlotlyChartData(trimmed, interactionId)];
    }

    // Otherwise assume it's matplotlib image payload (data URL or base64).
    return [buildFallbackMatplotlibChartData(figureJson, interactionId)];
}

function buildDataFromHistoryInteraction(
    dataframeJson?: string | null,
    figureJson?: string | null,
    interactionId?: string
): QueryResponse['data'] | undefined {
    const tablesData = buildDataFromDataframeJson(dataframeJson ?? undefined);
    const charts = buildChartsFromFigureJson(figureJson, interactionId);

    const hasTables = !!tablesData?.tables?.length;
    const hasCharts = charts.length > 0;

    if (!hasTables && !hasCharts) return undefined;

    return {
        tables: tablesData?.tables ?? [],
        charts,
        chat_responses: [],
    };
}

// --- HELPERS ---
const genLocalId = () => Math.random().toString(36).substring(2, 9);
const genSessionId = () => `session_${Date.now()}_${genLocalId()}`;

export { genLocalId, genSessionId };

function rowsToTableData(rows: any[] | undefined, title = 'Result'): QueryResponse['data'] | undefined {
    if (!rows || !Array.isArray(rows) || rows.length === 0) return undefined;
    const cols = Object.keys(rows[0] ?? {});
    return {
        tables: [{
            title,
            columns: cols,
            data: rows,
            row_count: rows.length
        }],
        charts: [],
        chat_responses: []
    };
}

export { rowsToTableData };

function mergeSessionsKeepExisting(existing: any[], incoming: any[]): any[] {
    const seen = new Set(existing.map((s: any) => s.id));
    // keep existing order; append only new remote ones
    return [...existing, ...incoming.filter((s: any) => !seen.has(s.id))];
}

export { mergeSessionsKeepExisting };

export {
    buildDataFromHistoryInteraction,
    buildChartsFromFigureJson,
};

